import { tripEmploymentSelection } from '../tripEmploymentSelection'
import { TripEmploymentField, type TripEmploymentChoice } from './TripEmploymentField'
import { EmploymentField, useEmploymentSelection } from './EmploymentField'
import { useEffect, useMemo, useState } from 'react'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../../i18n'
import { tone } from '../../../theme/tones'
import { Avatar, Button, Chip, DateField, FormInput, FormTextArea, Icon, OptionSelect } from '../../../ui'
import { DATE_RE, ymdPlusDays } from '../../../lib/date'
import { daysInclusive } from '../../../lib/format'
import { useMe } from '../../users/useUsers'
import { useUserSearch } from '../../users/useUserSearch'
import { useCreateBusinessTrip, useTripDestinations } from '../useEdo'
import { RoutePreview } from '../components/RouteTimeline'
import { Field, FormActions, MISSING_DATE_FROM, MISSING_DATE_TO, MISSING_WORKPLACE, dateCol, isWeb } from './shared'
import { api } from '../../../api/client'
import { pickFileWeb } from '../../../lib/files'
import type { TripTravellerInput } from '../../../lib/types'

// ── Business trip (ТЗ-2026-003) ─────────────────────────────────────────────────
type Traveller = { id: string; fullName: string; jobTitle?: string | null; firstName?: string | null; lastName?: string | null }
type TravellerDoc = { phone: string; validUntil: string; file: File | null; latinFirst: string; latinLast: string }

/**
 * Имя на билет — как `latinNameForTicket` на бэке (src/edo/latin-name.ts): диакритику снимаем
 * (İ→I, Ş→S), а у ə и ı разложения нет — они остаются, и такое имя форма не пропустит.
 */
const ticketName = (s?: string | null) =>
  (s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').normalize('NFC').trim().replace(/\s+/g, ' ')
const isTicketName = (s: string) => /^[A-Za-z'\- ]+$/.test(ticketName(s))

// Латинское ФИО подставляем из карточки уже в «билетном» виде: что видно в поле, то и уйдёт агенту.
const blankDoc = (m: Traveller): TravellerDoc => ({
  phone: '',
  validUntil: '',
  file: null,
  latinFirst: ticketName(m.firstName),
  latinLast: ticketName(m.lastName),
})

/**
 * Участники поездки (групповая командировка). Список отправляется целиком
 * (employeeIds = полный состав): по умолчанию — вы; себя можно убрать, если
 * оформляете поездку коллегам. Поиск — как ManagerPicker (debounce + /users?search).
 */
function TripMatesPicker({
  travellers,
  onChange,
}: {
  travellers: Traveller[]
  onChange: (next: Traveller[]) => void
}) {
  const { t } = useApp()
  const { q, setQ, debounced, hits } = useUserSearch()
  const chosen = new Set(travellers.map((m) => m.id))
  const options = useMemo(() => (hits ?? []).filter((u) => !chosen.has(u.id)).slice(0, 6), [hits, travellers])

  return (
    <YStack gap={8}>
      {/* выбранные — чипы с аватаром и крестиком */}
      <XStack gap={8} flexWrap="wrap">
        {travellers.map((m) => (
          <XStack
            key={m.id}
            alignItems="center"
            gap={7}
            backgroundColor="$bgLayout"
            borderWidth={1}
            borderColor="$borderColor"
            borderRadius={999}
            paddingLeft={5}
            paddingRight={9}
            paddingVertical={4}
          >
            <Avatar name={m.fullName} seed={m.id} size="sm" />
            <Text fontSize={13} fontWeight="600" color="$color" numberOfLines={1}>
              {m.fullName}
            </Text>
            <Stack onPress={() => onChange(travellers.filter((x) => x.id !== m.id))} cursor="pointer" padding={2}>
              <Icon name="x" size={13} color="$muted" />
            </Stack>
          </XStack>
        ))}
        {travellers.length === 0 && (
          <Text fontSize={12} color={tone('red').text}>
            {t({ ru: 'Добавьте хотя бы одного участника', en: 'Add at least one traveller' })}
          </Text>
        )}
      </XStack>

      <FormInput
        value={q}
        onChangeText={setQ}
        placeholder={t({ ru: 'Добавить коллегу (поиск по имени)…', en: 'Add a colleague (search by name)…' })}
      />
      {debounced.length >= 2 && options.length > 0 && (
        <YStack borderWidth={1} borderColor="$borderSecondary" borderRadius={7} overflow="hidden">
          {options.map((u, i) => (
            <XStack
              key={u.id}
              onPress={() => {
                onChange([...travellers, { id: u.id, fullName: u.fullName, jobTitle: u.jobTitle, firstName: u.firstName, lastName: u.lastName }])
                setQ('')
              }}
              cursor="pointer"
              alignItems="center"
              gap={8}
              padding={8}
              borderTopWidth={i === 0 ? 0 : 1}
              borderColor="$borderSecondary"
              hoverStyle={{ backgroundColor: '$backgroundHover' }}
            >
              <Avatar name={u.fullName} seed={u.id} size="sm" />
              <YStack flex={1} minWidth={0}>
                <Text fontSize={13} color="$color" numberOfLines={1}>
                  {u.fullName}
                </Text>
                {!!u.jobTitle && (
                  <Text fontSize={11} color="$muted" numberOfLines={1}>
                    {u.jobTitle}
                  </Text>
                )}
              </YStack>
              <Icon name="plus" size={14} color="$primary" />
            </XStack>
          ))}
        </YStack>
      )}
    </YStack>
  )
}

/**
 * Направление командировки: страна — выпадающий список с поиском (весь справочник норм 1С
 * по алфавиту), город — норм-города чипами / свой ввод. Названия стран в 1С азербайджанские
 * («Gürcüstan»): поиск в списке понимает их без диакритики, а сам список виден целиком.
 */
type TripDest = { stranaId: string; country: string; iso: string | null; city: string; perDiem: number; currency: string }
function TripDestinationPicker({
  value,
  onChange,
}: {
  value: TripDest | null
  onChange: (v: TripDest | null) => void
}) {
  const { t } = useApp()
  const { data: dests } = useTripDestinations()
  const list = dests ?? []
  const selected = value?.stranaId ? list.find((d) => d.stranaId === value.stranaId) ?? null : null
  const countries = useMemo(
    () =>
      [...(dests ?? [])]
        .sort((a, b) => a.country.localeCompare(b.country, 'az'))
        .map((d) => ({ value: d.stranaId, label: d.country })),
    [dests],
  )

  return (
    <YStack gap={8}>
      <OptionSelect
        list
        searchable
        options={countries}
        value={selected ? [selected.stranaId] : []}
        onChange={([id]) => {
          const d = list.find((x) => x.stranaId === id)
          // Другая страна — город выбирается заново; повторный выбор той же город не сбрасывает.
          if (d && d.stranaId !== value?.stranaId) onChange({ stranaId: d.stranaId, country: d.country, iso: d.iso, city: '', perDiem: 0, currency: '' })
        }}
        placeholder={t({ ru: 'Выберите страну', en: 'Choose a country', az: 'Ölkəni seçin' })}
        highlight={!selected}
      />

      {/* город: норм-города чипами (несут суточную) + свой ввод для прочих городов */}
      {selected && (
        <YStack gap={6}>
          <XStack gap={6} flexWrap="wrap">
            {/* Паддинги переопределены: у города чип на 1px компактнее дефолтного Chip. */}
            {selected.cities.map((c) => (
              <Chip
                key={c.city}
                on={c.city === value?.city}
                dim
                onPress={() => onChange({ stranaId: selected.stranaId, country: selected.country, iso: selected.iso, city: c.city, perDiem: c.perDiem, currency: c.currency })}
                paddingHorizontal={12}
                paddingVertical={6}
              >
                {c.city}
              </Chip>
            ))}
          </XStack>
          <FormInput
            value={value?.city ?? ''}
            onChangeText={(v) => {
              const norm = selected.cities.find((c) => c.city === v)
              onChange({ stranaId: selected.stranaId, country: selected.country, iso: selected.iso, city: v, perDiem: norm?.perDiem ?? 0, currency: norm?.currency ?? '' })
            }}
            maxLength={200}
            placeholder={t({ ru: 'Город (или свой, если нет в списке)', en: 'City (or type your own)' })}
          />
        </YStack>
      )}
    </YStack>
  )
}

export function BusinessTripForm({ onDone }: { onDone: () => void }) {
  const employment = useEmploymentSelection()
  const { t } = useApp()
  const { data: me } = useMe()
  const create = useCreateBusinessTrip(onDone)

  const [travellers, setTravellers] = useState<Traveller[]>([])
  const [targetChoices, setTargetChoices] = useState<Record<string, TripEmploymentChoice & { scope: string }>>({})
  const companyId = employment.jobs.find(job => job.id === employment.employmentId)?.companyId
  const scope = `${employment.employmentId ?? ''}:${travellers.map(user => user.id).join(',')}`
  const targets = travellers.filter(user => user.id !== me?.id)
  const targetSelection = tripEmploymentSelection(travellers.map(user => user.id), me?.id, employment.employmentId, companyId, scope, targetChoices)
  const [seeded, setSeeded] = useState(false)
  // По умолчанию едет сам инициатор — как только профиль загрузился, сажаем его чипом.
  useEffect(() => {
    if (!seeded && me) {
      setTravellers([{ id: me.id, fullName: me.fullName, jobTitle: me.jobTitle, firstName: me.firstName, lastName: me.lastName }])
      setSeeded(true)
    }
  }, [me, seeded])

  // Направление из пикера (зеркало норм 1С): страна → strana_id (GUID), город + суточная норма.
  const [dest, setDest] = useState<TripDest | null>(null)
  // Полный адрес прибытия (v1.2 Блок 2) — уточнение + данные для трансфера, необязателен.
  const [address, setAddress] = useState('')
  // Откуда выезжаем: направление у заявки было, точки выезда — не было, а без неё не купить билет.
  const [origin, setOrigin] = useState('')
  // Данные участников для брони: в карточке их нет (телефон заполнен у 12%, паспорта нет вовсе).
  const [docs, setDocs] = useState<Record<string, TravellerDoc>>({})
  const [purpose, setPurpose] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [dateReturn, setDateReturn] = useState('')
  const [comment, setComment] = useState('')

  // Зарубежная ли поездка — по ISO из справочника, НЕ по названию страны: в базах 1С
  // Азербайджан записан тремя разными способами. Страны без ISO сервер тоже считает зарубежной.
  const foreign = !!dest && dest.iso?.toUpperCase() !== 'AZ'
  const docOf = (m: Traveller) => docs[m.id] ?? blankDoc(m)
  const passportsOk = !foreign || travellers.every((m) => !!docOf(m).file && DATE_RE.test(docOf(m).validUntil))
  // Латинское ФИО нужно на любой билет, не только зарубежный — сервер требует его у всех участников.
  const latinOk = travellers.every((m) => isTicketName(docOf(m).latinFirst) && isTicketName(docOf(m).latinLast))
  const countryOk = !!dest?.stranaId
  const cityOk = !!dest?.city.trim()
  const datesOk = DATE_RE.test(dateFrom) && DATE_RE.test(dateTo) && dateTo >= dateFrom
  const returnOk = DATE_RE.test(dateReturn) && (!datesOk || dateReturn >= dateTo)
  const duration = datesOk ? daysInclusive(dateFrom, dateTo) : 0

  // Суточные с РАЗБИВКОЙ по каждому участнику + итог (ТЗ v1.2 Блок 3). Норма — зеркальная из
  // регистра 1С по выбранному городу; направление одно, поэтому ставка у всех одинаковая.
  // Пересчитывается сама при смене состава/дат/города. Свой город (нормы нет) → perDiem=0, блок скрыт.
  const perDiem = dest?.perDiem ?? 0
  const lines =
    perDiem > 0 && duration > 0
      ? travellers.map((m) => ({ id: m.id, name: m.fullName, amount: perDiem * duration }))
      : []
  const estTotal = lines.reduce((s, l) => s + l.amount, 0)
  // Чего не хватает — теми же проверками, что держат кнопку: подсказка не разойдётся с ней.
  const missing = [
    !employment.ready && t(MISSING_WORKPLACE),
    travellers.length === 0 && t({ ru: 'Кто едет', en: 'Travellers', az: 'Kim gedir' }),
    employment.ready && !targetSelection.ready && t({ ru: 'Места работы участников', en: 'Participant workplaces', az: 'İştirakçıların iş yerləri' }),
    !countryOk && t({ ru: 'Страна', en: 'Country', az: 'Ölkə' }),
    !cityOk && t({ ru: 'Город', en: 'City', az: 'Şəhər' }),
    origin.trim() === '' && t({ ru: 'Откуда', en: 'Origin', az: 'Haradan' }),
    !latinOk && t({ ru: 'ФИО участников латиницей', en: 'Travellers’ names in Latin letters', az: 'İştirakçıların adı latın hərfləri ilə' }),
    !passportsOk && t({ ru: 'Паспорт участников (для зарубежной поездки)', en: 'Participant passports (foreign trip)', az: 'İştirakçıların pasportu' }),
    purpose.trim() === '' && t({ ru: 'Цель командировки', en: 'Trip purpose', az: 'Ezamiyyətin məqsədi' }),
    !DATE_RE.test(dateFrom) && t(MISSING_DATE_FROM),
    !DATE_RE.test(dateTo) && t(MISSING_DATE_TO),
    !DATE_RE.test(dateReturn) && t({ ru: 'Выход на работу', en: 'Back to work', az: 'İşə çıxış' }),
  ].filter((x): x is string => !!x)
  const ok = missing.length === 0 && datesOk && returnOk && purpose.length <= 1000 &&
    comment.length <= 1000 && address.length <= 500 && origin.length <= 200 && !create.isPending

  /**
   * Копии паспортов грузим ДО подачи и шлём uploadId — тот же порядок, что у справки отцовского
   * отпуска. Файл, загруженный к заявке, которая потом не создалась, остаётся сиротой в MinIO;
   * это дешевле, чем создать заявку и не смочь привязать к ней паспорт.
   */
  const submit = async () => {
    if (!ok || !dest) return
    const travellerData: TripTravellerInput[] = []
    for (const m of travellers) {
      const d = docOf(m)
      let passportUploadId: string | undefined
      if (d.file) {
        const form = new FormData()
        form.append('file', d.file as unknown as Blob)
        passportUploadId = (await api.uploadEdoDoc(form)).id
      }
      travellerData.push({
        employeeId: m.id,
        phone: d.phone.trim() || undefined,
        passportValidUntil: DATE_RE.test(d.validUntil) ? d.validUntil : undefined,
        passportUploadId,
        latinFirstName: d.latinFirst.trim(),
        latinLastName: d.latinLast.trim(),
      })
    }
    create.mutate({
      employmentId: employment.employmentId,
      employeeIds: travellers.map((m) => m.id),
      employeeEmploymentIds: targetSelection.employeeEmploymentIds,
      originCityName: origin.trim(),
      travellers: travellerData,
      destinationCountryCode: dest.stranaId, // GUID страны 1С (strana_id) — резолвер примет
      destinationCityName: dest.city.trim(),
      destinationAddress: address.trim() || undefined,
      purpose: purpose.trim(),
      dateFrom,
      dateTo,
      dateOfReturnToWork: dateReturn,
      comment: comment.trim() || undefined,
    })
  }

  // Первая нарушенная клиентская проверка → подсказка (серверный message главнее при сабмите).
  const hint =
    datesOk && DATE_RE.test(dateReturn) && dateReturn < dateTo
      ? t({ ru: 'Дата выхода на работу не может быть раньше окончания поездки', en: 'Return-to-work date cannot precede the trip end' })
      : ''

  return (
    <YStack gap={14}>
      <EmploymentField selection={employment} />
      {/* Суммы не вводятся: суточные/средний считает 1С (контракт 08.07.2026) */}
      <XStack
        alignItems="center"
        gap={8}
        backgroundColor="$primaryBg"
        borderWidth={1}
        borderColor="$primary"
        borderRadius={7}
        paddingHorizontal={12}
        paddingVertical={9}
      >
        <Icon name="info" size={15} color="$primary" />
        <Text fontSize={12} color="$color" flex={1}>
          {t({
            ru: 'Суточные показаны по нормам 1С — суммы вводить не нужно. Окончательный расчёт делают бухгалтер и CFO.',
            en: 'Per-diem is shown from the 1C norms — no amounts to enter. Final figures are set by Accounting and the CFO.',
          })}
        </Text>
      </XStack>

      <Field label={t({ ru: 'Кто едет', en: 'Travellers' })} required>
        <TripMatesPicker travellers={travellers} onChange={setTravellers} />
      </Field>

      {companyId && targets.map(user => <TripEmploymentField key={`${scope}:${user.id}`} user={user} companyId={companyId} onSelection={choice => setTargetChoices(current => {
        const old = current[user.id]
        if (old?.scope === scope && old.ready === choice.ready && old.id === choice.id && old.requiresChoice === choice.requiresChoice) return current
        return { ...current, [user.id]: { ...choice, scope } }
      })} />)}
      {employment.ready && !targetSelection.ready && <Text color="$muted">{t({ ru: 'Для выбранного состава не удалось подтвердить все места работы. Обратитесь в HR или измените состав участников.', az: 'Bütün iştirakçıların iş yerləri təsdiqlənmədi. HR ilə əlaqə saxlayın.', en: 'Not all participant workplaces could be verified. Contact HR or change participants.' })}</Text>}
      {/* откуда: точки выезда в заявке не было, а без неё билет не купить */}
      <Field label={t({ ru: 'Откуда', en: 'Origin', az: 'Haradan' })} required>
        <FormInput
          value={origin}
          onChangeText={setOrigin}
          maxLength={200}
          placeholder={t({ ru: 'Город выезда, например Баку', en: 'Departure city, e.g. Baku', az: 'Çıxış şəhəri' })}
        />
      </Field>

      {/* направление: страна/город из зеркала норм 1С (пикер шлёт strana_id) */}
      <Field label={t({ ru: 'Направление (страна · город)', en: 'Destination (country · city)' })} required>
        <TripDestinationPicker value={dest} onChange={setDest} />
      </Field>

      {/* данные участников для брони: в карточке их нет, вводит инициатор */}
      {travellers.length > 0 && (
        <Field
          label={t({ ru: 'Данные участников для брони', en: 'Traveller details for booking', az: 'Bron üçün iştirakçı məlumatları' })}
          required
        >
          <YStack gap={10}>
            {foreign && (
              <Text fontSize={12} color="$muted">
                {t({
                  ru: 'Зарубежная поездка: копия паспорта и срок его действия обязательны на каждого участника.',
                  en: 'Foreign trip: a passport copy and its expiry date are required for every traveller.',
                })}
              </Text>
            )}
            {travellers.map((m) => {
              const d = docOf(m)
              const set = (patch: Partial<TravellerDoc>) =>
                setDocs((cur) => ({ ...cur, [m.id]: { ...(cur[m.id] ?? blankDoc(m)), ...patch } }))
              const firstOk = isTicketName(d.latinFirst)
              const lastOk = isTicketName(d.latinLast)
              return (
                <YStack key={m.id} gap={7} borderWidth={1} borderColor="$borderColor" borderRadius={8} padding={10}>
                  <Text fontSize={13} fontWeight="600" color="$color">
                    {m.fullName}
                  </Text>
                  {/* имя на билет: из карточки, если там латиница; у трети карточек его нет вовсе */}
                  <XStack gap={10} flexWrap="wrap">
                    <YStack flex={1} minWidth={150} gap={4}>
                      <Text fontSize={11} color="$muted">
                        {t({ ru: 'Имя латиницей, как в паспорте', en: 'First name as in passport', az: 'Ad latınca, pasportdakı kimi' })}
                      </Text>
                      <FormInput
                        value={d.latinFirst}
                        onChangeText={(v) => set({ latinFirst: v })}
                        maxLength={100}
                        placeholder="Ismayil"
                        borderColor={firstOk ? '$borderColor' : tone('red').solid}
                      />
                    </YStack>
                    <YStack flex={1} minWidth={150} gap={4}>
                      <Text fontSize={11} color="$muted">
                        {t({ ru: 'Фамилия латиницей, как в паспорте', en: 'Last name as in passport', az: 'Soyad latınca, pasportdakı kimi' })}
                      </Text>
                      <FormInput
                        value={d.latinLast}
                        onChangeText={(v) => set({ latinLast: v })}
                        maxLength={100}
                        placeholder="Masimov"
                        borderColor={lastOk ? '$borderColor' : tone('red').solid}
                      />
                    </YStack>
                  </XStack>
                  {(!firstOk || !lastOk) && (
                    <Text fontSize={11} color={tone('red').text}>
                      {t({
                        ru: 'Латинскими буквами — так, как в паспорте',
                        en: 'Latin letters only — as in the passport',
                        az: 'Latın hərfləri ilə — pasportdakı kimi',
                      })}
                    </Text>
                  )}
                  <XStack gap={10} flexWrap="wrap">
                    <YStack flex={1} minWidth={150} gap={4}>
                      <Text fontSize={11} color="$muted">
                        {t({ ru: 'Телефон', en: 'Phone', az: 'Telefon' })}
                      </Text>
                      <FormInput
                        value={d.phone}
                        onChangeText={(v) => set({ phone: v })}
                        maxLength={32}
                        placeholder="+994 ..."
                      />
                    </YStack>
                    {foreign && (
                      <YStack {...dateCol} gap={4}>
                        <Text fontSize={11} color="$muted">
                          {t({ ru: 'Паспорт действителен до', en: 'Passport valid until', az: 'Pasportun etibarlılıq müddəti' })}
                        </Text>
                        <DateField
                          value={d.validUntil}
                          onChange={(v) => set({ validUntil: v })}
                          placeholder="ГГГГ-ММ-ДД"
                          highlight={!d.validUntil}
                        />
                      </YStack>
                    )}
                  </XStack>
                  {foreign &&
                    (isWeb ? (
                      <Button
                        variant="default"
                        block
                        icon={<Icon name="paperclip" size={15} color="$muted" />}
                        onPress={async () => set({ file: await pickFileWeb('.pdf,.jpg,.jpeg,.png') })}
                      >
                        {d.file
                          ? `📎 ${d.file.name}`
                          : t({ ru: 'Копия паспорта (PDF/JPG/PNG)', en: 'Passport copy (PDF/JPG/PNG)' })}
                      </Button>
                    ) : (
                      <Text fontSize={12} color="$muted">
                        {t({
                          ru: 'Прикрепление копии паспорта доступно в веб-версии портала',
                          en: 'Attaching a passport copy is available in the web version',
                        })}
                      </Text>
                    ))}
                </YStack>
              )
            })}
          </YStack>
        </Field>
      )}

      {/* полный адрес прибытия (v1.2 Блок 2): уточнение города + данные для трансфера */}
      <Field label={t({ ru: 'Полный адрес прибытия (необязательно)', en: 'Full arrival address (optional)' })}>
        <FormTextArea
          value={address}
          onChangeText={setAddress}
          maxLength={500}
          placeholder={t({
            ru: 'Город / область / район / улица — если нужного города нет в списке или нужен трансфер',
            en: 'City / region / district / street — if the city is not listed or a transfer is needed',
          })}
          minHeight={52}
        />
      </Field>

      <Field label={t({ ru: 'Цель командировки', en: 'Trip purpose' })} required>
        <FormTextArea
          value={purpose}
          onChangeText={setPurpose}
          maxLength={1000}
          placeholder={t({ ru: 'Например: деловые переговоры с партнёром', en: 'e.g. business negotiations with a partner' })}
        />
      </Field>

      {/* даты: поездка с/по + выход на работу (требование контракта 1С) */}
      <XStack gap={12} flexWrap="wrap">
        <YStack {...dateCol} gap={6}>
          <Field label={t({ ru: 'С', en: 'From' })} required>
            <DateField
              value={dateFrom}
              onChange={(d) => {
                setDateFrom(d)
                if (dateTo && dateTo < d) setDateTo('')
              }}
              placeholder="ГГГГ-ММ-ДД"
              highlight={!dateFrom}
            />
          </Field>
        </YStack>
        <YStack {...dateCol} gap={6}>
          <Field label={t({ ru: 'По', en: 'To' })} required>
            <DateField
              value={dateTo}
              onChange={(d) => {
                setDateTo(d)
                // Дата выхода по умолчанию — следующий день после окончания (можно поменять).
                if (!dateReturn || dateReturn < d) setDateReturn(ymdPlusDays(d, 1))
              }}
              min={dateFrom || undefined}
              placeholder="ГГГГ-ММ-ДД"
              highlight={!dateTo}
            />
          </Field>
        </YStack>
        <YStack {...dateCol} gap={6}>
          <Field label={t({ ru: 'Выход на работу', en: 'Back to work' })} required>
            <DateField
              value={dateReturn}
              onChange={setDateReturn}
              min={dateTo || undefined}
              placeholder="ГГГГ-ММ-ДД"
              highlight={!dateReturn}
            />
          </Field>
        </YStack>
      </XStack>

      {duration > 0 && (
        <XStack alignItems="center" gap={7}>
          <Icon name="calendar" size={14} color="$muted" />
          <Text fontSize={12} fontWeight="600" color="$muted">
            {t({ ru: `Длительность: ${duration} дн.`, en: `Duration: ${duration} d`, az: `Müddət: ${duration} gün` })}
            {travellers.length > 1
              ? t({ ru: ` · групповая (${travellers.length} чел.)`, en: ` · group (${travellers.length} ppl)`, az: ` · qrup (${travellers.length} nəfər)` })
              : ''}
          </Text>
        </XStack>
      )}

      {/* суточные: разбивка по каждому участнику + ИТОГО (ТЗ v1.2 Блок 3, §4.2) */}
      {lines.length > 0 && dest && (
        <YStack
          gap={7}
          backgroundColor="$bgLayout"
          borderWidth={1}
          borderColor="$borderColor"
          borderRadius={7}
          paddingHorizontal={12}
          paddingVertical={10}
        >
          <XStack alignItems="center" gap={7}>
            <Icon name="wallet" size={15} color="$primary" />
            <Text fontSize={12} fontWeight="700" color="$color" flex={1}>
              {t({ ru: 'Суточные', en: 'Per-diem' })}
            </Text>
            <Text fontSize={11} color="$muted">
              {t({
                ru: `${dest.perDiem} ${dest.currency}/сут (норма 1С) × ${duration} дн.`,
                en: `${dest.perDiem} ${dest.currency}/day (1C norm) × ${duration} d`,
                az: `${dest.perDiem} ${dest.currency}/gün (1С norması) × ${duration} gün`,
              })}
            </Text>
          </XStack>

          {lines.map((l) => (
            <XStack key={l.id} alignItems="center" gap={8}>
              <Text fontSize={12} color="$muted" flex={1} numberOfLines={1}>
                {l.name}
              </Text>
              <Text fontSize={12} fontWeight="600" color="$color">
                {`${l.amount.toLocaleString('ru-RU')} ${dest.currency}`}
              </Text>
            </XStack>
          ))}

          <XStack
            alignItems="center"
            gap={8}
            borderTopWidth={1}
            borderColor="$borderColor"
            paddingTop={7}
          >
            <Text fontSize={12} fontWeight="700" color="$color" flex={1}>
              {t({ ru: 'ИТОГО', en: 'TOTAL' })}
            </Text>
            <Text fontSize={13} fontWeight="700" color="$primary">
              {`${estTotal.toLocaleString('ru-RU')} ${dest.currency}`}
            </Text>
          </XStack>

          <Text fontSize={11} color="$muted">
            {t({
              ru: 'Предварительно; окончательные суммы — у бухгалтера и CFO',
              en: 'Preliminary; final figures are set by Accounting and the CFO',
            })}
          </Text>
        </YStack>
      )}

      <Field label={t({ ru: 'Комментарий (необязательно)', en: 'Comment (optional)' })}>
        <FormTextArea
          value={comment}
          onChangeText={setComment}
          maxLength={1000}
          placeholder={t({ ru: 'Например: встреча с подрядчиком', en: 'e.g. contractor meeting' })}
        />
      </Field>

      {hint !== '' && (
        <Text fontSize={12} color={tone('red').text}>
          {hint}
        </Text>
      )}

      <RoutePreview type="BUSINESS_TRIP" employmentId={employment.employmentId} enabled={employment.ready} />
      <FormActions onCancel={onDone} onSubmit={submit} ok={ok} m={create} missing={missing} />
    </YStack>
  )
}
